/**
 * @fileoverview Goal step component for the analysis wizard - content type, platform, and intent selection
 */

import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import { TrebbleTitle, TrebbleText } from '../../common/TrebbleTypography.tsx';
import TrebbleBadge from '../../common/TrebbleBadge.tsx';
import TrebbleSelect from '../../common/TrebbleSelect.tsx';
import TrebbleInput from '../../common/TrebbleInput.tsx';
import TrebbleTooltip from '../../common/TrebbleTooltip.tsx';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';
// Alternative import methods if the above doesn't work:
// const ti18n = require('i18n!nls/Deepcuti18n');
// import * as ti18n from 'i18n!nls/Deepcuti18n';
import { CONFIDENCE_THRESHOLD_UNCERTAIN, CONFIDENCE_THRESHOLD_MEDIUM } from '../constants/schema.js';
import ReferenceProjectPicker from './ReferenceProjectPicker.jsx';

const { Option } = TrebbleSelect;

/**
 * @typedef {import('../types/deepcut.js').AnalysisResults} AnalysisResults
 */

/**
 * Goal step component for setting up content analysis parameters
 * @param {Object} props - Component props
 * @param {AnalysisResults} props.results - Analysis results with suggested intents
 * @param {string} props.selectedIntent - Currently selected intent
 * @param {string} props.customIntent - Custom intent text
 * @param {string} props.userDirectivesText - User directives text
 * @param {string} props.targetPlatform - Selected target platform
 * @param {string} props.customPlatform - Custom platform text
 * @param {string} props.selectedContentType - Selected content type
 * @param {string} props.customContentType - Custom content type text
 * @param {Function} props.onSelectedIntentChange - Callback for intent selection change
 * @param {Function} props.onCustomIntentChange - Callback for custom intent change
 * @param {Function} props.onUserDirectivesChange - Callback for user directives change
 * @param {Function} props.onTargetPlatformChange - Callback for platform change
 * @param {Function} props.onCustomPlatformChange - Callback for custom platform change
 * @param {Function} props.onSelectedContentTypeChange - Callback for content type change
 * @param {Function} props.onCustomContentTypeChange - Callback for custom content type change
 * @returns {React.ReactElement}
 */
export default function GoalStep({
  results,
  selectedIntent,
  customIntent,
  userDirectivesText,
  targetPlatform,
  customPlatform,
  selectedContentType,
  customContentType,
  onSelectedIntentChange,
  onCustomIntentChange,
  onUserDirectivesChange,
  onTargetPlatformChange,
  onCustomPlatformChange,
  onSelectedContentTypeChange,
  onCustomContentTypeChange,
  // Edit Like Me props
  isEditLikeMeMode = false,
  selectedReferenceProject = null,
  onReferenceProjectChange,
  prefetchedProjects = null,
}) {
  // Content type options with their guidance
  const contentTypeOptions = [
    { value: window.getI18n(ti18n, 'EDUCATIONAL_VIDEO'), guidance: window.getI18n(ti18n, 'EDUCATIONAL_VIDEO_GUIDANCE') },
    { value: window.getI18n(ti18n, 'BUSINESS_PRESENTATION'), guidance: window.getI18n(ti18n, 'BUSINESS_PRESENTATION_GUIDANCE') },
    { value: window.getI18n(ti18n, 'PERSONAL_VLOG'), guidance: window.getI18n(ti18n, 'PERSONAL_VLOG_GUIDANCE') },
    { value: window.getI18n(ti18n, 'INTERVIEW'), guidance: window.getI18n(ti18n, 'INTERVIEW_GUIDANCE') },
    { value: window.getI18n(ti18n, 'ENTERTAINMENT_CONTENT'), guidance: window.getI18n(ti18n, 'ENTERTAINMENT_CONTENT_GUIDANCE') },
    { value: window.getI18n(ti18n, 'PROFESSIONAL_TALK'), guidance: window.getI18n(ti18n, 'PROFESSIONAL_TALK_GUIDANCE') },
    { value: window.getI18n(ti18n, 'TUTORIAL'), guidance: window.getI18n(ti18n, 'TUTORIAL_GUIDANCE') },
    { value: window.getI18n(ti18n, 'PODCAST_EPISODE'), guidance: window.getI18n(ti18n, 'PODCAST_EPISODE_GUIDANCE') },
    { value: window.getI18n(ti18n, 'WEBINAR'), guidance: window.getI18n(ti18n, 'WEBINAR_GUIDANCE') },
    { value: window.getI18n(ti18n, 'SALES_PITCH'), guidance: window.getI18n(ti18n, 'SALES_PITCH_GUIDANCE') }
  ];

  return (
    <>
      <style>{`
        .deepcut-textarea::placeholder,
        textarea.deepcut-textarea::placeholder,
        .ant-input.deepcut-textarea::placeholder {
          color: var(--text-default-color, #6B7280) !important;
          opacity: 0.5 !important;
        }
      `}</style>
    <TrebbleSpace direction="vertical" className="w-full">
      <div className="mb-6">
        <TrebbleTitle level={3} className="m-0">
          {isEditLikeMeMode
            ? (window.getI18n(ti18n, 'EDIT_LIKE_ME_STEP_TITLE') || 'Choose a project to use as your editing reference.')
            : window.getI18n(ti18n, 'GOAL_STEP_TITLE')}
        </TrebbleTitle>
      </div>

      {/* Reference Project Picker — shown in both modes (required in Edit Like Me, optional in DeepCut) */}
      <ReferenceProjectPicker
        value={selectedReferenceProject}
        onChange={(projectId, projectData) => {
          if (onReferenceProjectChange) {
            onReferenceProjectChange(projectId, projectData);
          }
        }}
        required={isEditLikeMeMode}
        prefetchedProjects={prefetchedProjects}
      />

      {/* Regular DeepCut fields — hidden in Edit Like Me mode */}
      <div style={isEditLikeMeMode ? { display: 'none' } : {}}>

      <div className="mb-5">
        <TrebbleText strong className="mb-3 flex items-center gap-2 text-base">
          {window.getI18n(ti18n, 'RECORDING_TYPE_QUESTION')}
          <TrebbleTooltip title={window.getI18n(ti18n, 'RECORDING_TYPE_TOOLTIP')} placement="right">
            <InfoCircleOutlined style={{ color: 'var(--trebble-primary)', fontSize: 16, marginLeft: 4 }} />
          </TrebbleTooltip>
        </TrebbleText>
        <TrebbleSelect
          value={selectedContentType}
          onChange={(value) => {
            onSelectedContentTypeChange(value);
            if (value !== 'Custom') {
              onCustomContentTypeChange('');
            }
          }}
          className="w-full"
          placeholder={window.getI18n(ti18n, 'SELECT_CONTENT_TYPE_PLACEHOLDER')}
          allowClear
          optionLabelProp="label"
        >
          {contentTypeOptions.map(option => (
            <Option key={option.value} value={option.value} label={option.value}>
              <div className="py-1">
                <TrebbleText strong size="small" className="text-sm">{option.value}</TrebbleText>
                <br />
                <TrebbleText type="secondary" size="small" className="text-xs mt-0.5 block">
                  {option.guidance}
                </TrebbleText>
              </div>
            </Option>
          ))}
          <Option value="Custom" label={window.getI18n(ti18n, 'CUSTOM_CONTENT_TYPE_LABEL')}>
            <div className="py-1">
              <TrebbleText strong size="small" className="text-sm">{window.getI18n(ti18n, 'CUSTOM_CONTENT_TYPE_LABEL')}</TrebbleText>
              <br />
              <TrebbleText type="secondary" size="small" className="text-xs">
                {window.getI18n(ti18n, 'DEFINE_OWN_CONTENT_TYPE')}
              </TrebbleText>
            </div>
          </Option>
        </TrebbleSelect>
        {selectedContentType === 'Custom' && (
          <TrebbleInput
            value={customContentType}
            onChange={(e) => onCustomContentTypeChange(e.target.value)}
            placeholder={window.getI18n(ti18n, 'DESCRIBE_CONTENT_TYPE_PLACEHOLDER')}
            className="mt-2 deepcut-textarea"
          />
        )}
      </div>

      <div className="mb-5">
        <TrebbleText strong className="mb-3 flex items-center gap-2 text-base">
          {window.getI18n(ti18n, 'PLATFORM_QUESTION')}
          <TrebbleTooltip title={window.getI18n(ti18n, 'PLATFORM_TOOLTIP')} placement="right">
            <InfoCircleOutlined style={{ color: 'var(--trebble-primary)', fontSize: 16, marginLeft: 4 }} />
          </TrebbleTooltip>
        </TrebbleText>
        <TrebbleSelect
          value={targetPlatform}
          onChange={(value) => {
            onTargetPlatformChange(value);
            if (value !== 'Other') {
              onCustomPlatformChange('');
            }
          }}
          className="w-full"
          placeholder={window.getI18n(ti18n, "CHOOSE_YOUR_PLATFORM")}
          allowClear
        >
          <Option value={window.getI18n(ti18n, 'PLATFORM_YOUTUBE')}>{window.getI18n(ti18n, 'PLATFORM_YOUTUBE')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_PODCAST')}>{window.getI18n(ti18n, 'PLATFORM_PODCAST')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_LINKEDIN')}>{window.getI18n(ti18n, 'PLATFORM_LINKEDIN')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_INSTAGRAM')}>{window.getI18n(ti18n, 'PLATFORM_INSTAGRAM')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_TIKTOK')}>{window.getI18n(ti18n, 'PLATFORM_TIKTOK')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_FACEBOOK')}>{window.getI18n(ti18n, 'PLATFORM_FACEBOOK')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_WEBINAR')}>{window.getI18n(ti18n, 'PLATFORM_WEBINAR')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_ONLINE_COURSE')}>{window.getI18n(ti18n, 'PLATFORM_ONLINE_COURSE')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_WEBSITE_BLOG')}>{window.getI18n(ti18n, 'PLATFORM_WEBSITE_BLOG')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_NEWSLETTER_EMAIL')}>{window.getI18n(ti18n, 'PLATFORM_NEWSLETTER_EMAIL')}</Option>
          <Option value={window.getI18n(ti18n, 'PLATFORM_OTHER')}>{window.getI18n(ti18n, 'PLATFORM_OTHER')}</Option>
        </TrebbleSelect>
        {targetPlatform === window.getI18n(ti18n, 'PLATFORM_OTHER') && (
          <TrebbleInput
            value={customPlatform}
            onChange={(e) => onCustomPlatformChange(e.target.value)}
            placeholder={window.getI18n(ti18n, 'ENTER_YOUR_PLATFORM')}
            className="mt-2 deepcut-textarea"
          />
        )}
      </div>

      <div className="mb-5">
        <TrebbleText strong className="mb-3 flex items-center gap-2 text-base">
          {window.getI18n(ti18n, 'GOAL_QUESTION')}
          <TrebbleTooltip title={window.getI18n(ti18n, 'GOAL_TOOLTIP')} placement="right">
            <InfoCircleOutlined style={{ color: 'var(--trebble-primary)', fontSize: 16, marginLeft: 4 }} />
          </TrebbleTooltip>
        </TrebbleText>
        <TrebbleSelect
          value={selectedIntent}
          onChange={(value) => {
            onSelectedIntentChange(value);
            if (value !== 'custom') {
              onCustomIntentChange('');
            }
          }}
          className="w-full"
          placeholder={window.getI18n(ti18n, 'WHAT_TO_ACHIEVE_PLACEHOLDER')}
          allowClear
          optionLabelProp="label"
        >
          {(results.suggestedIntents || []).map(intent => (
            <Option key={intent.code} value={intent.code} label={intent.label}>
              <div className="py-1">
                <div className="flex items-center justify-between">
                  <TrebbleText strong size="small" className="text-sm">{intent.label}</TrebbleText>
                  <TrebbleBadge 
                  className='hidden'
                    count={`${Math.round(intent.confidence * 100)}%`} 
                    color={intent.confidence >= CONFIDENCE_THRESHOLD_UNCERTAIN ? 'green' : intent.confidence >= CONFIDENCE_THRESHOLD_MEDIUM ? 'orange' : 'default'} 
                  />
                </div>
                <TrebbleText type="secondary" size="small" className="text-xs mt-0.5 block">
                  {intent.rationale}
                </TrebbleText>
              </div>
            </Option>
          ))}
          <Option value="custom" label={window.getI18n(ti18n, 'CUSTOM_GOAL_LABEL')}>
            <div className="py-1">
              <TrebbleText strong size="small" className="text-sm">{window.getI18n(ti18n, 'CUSTOM_GOAL_LABEL')}</TrebbleText>
              <br />
              <TrebbleText type="secondary" size="small" className="text-xs">
                {window.getI18n(ti18n, 'CREATE_CUSTOM_GOAL')}
              </TrebbleText>
            </div>
          </Option>
        </TrebbleSelect>
        {selectedIntent === 'custom' && (
          <TrebbleInput
            value={customIntent}
            onChange={(e) => onCustomIntentChange(e.target.value)}
            placeholder={window.getI18n(ti18n, 'DESCRIBE_GOAL_PLACEHOLDER')}
            className="mt-2 deepcut-textarea"
          />
        )}
      </div>

      <div className="mb-5">
        <TrebbleText strong className="mb-3 flex items-center gap-2 text-base">
          {window.getI18n(ti18n, 'REQUIREMENTS_QUESTION')}
          <TrebbleTooltip title={window.getI18n(ti18n, 'REQUIREMENTS_TOOLTIP')} placement="right">
            <InfoCircleOutlined style={{ color: 'var(--trebble-primary)', fontSize: 16, marginLeft: 4 }} />
          </TrebbleTooltip>
        </TrebbleText>
        <TrebbleInput.TextArea
          rows={3}
          placeholder={window.getI18n(ti18n, 'REQUIREMENTS_PLACEHOLDER')}
          value={userDirectivesText}
          onChange={(e) => onUserDirectivesChange(e.target.value)}
          className="leading-relaxed deepcut-textarea"
          style={{ fontFamily: 'inherit' }}
        />
      </div>

      </div>{/* End of regular DeepCut fields wrapper */}
    </TrebbleSpace>
    </>
  );
} 